import { Dialog } from "@headlessui/react";

import { JSX } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import Button from "@/components/buttons/Button.tsx";
import CircleButton from "@/components/buttons/CircleButton.tsx";
import FilterListbox from "@/components/catalog/FilterListbox.tsx";
import ModalProvider from "@/components/helpers/Modal.tsx";
import { IconFilter, IconX } from "@/components/icons";

import { useGetCatalogFilter } from "@/hooks/api/content.hooks.ts";
import { cn } from "@/utils/cn.ts";

const FilterMobileMenu = (): JSX.Element => {
  const { t } = useTranslation();
  const { id } = useParams() as { id: string };
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: filters } = useGetCatalogFilter(parseInt(id));

  const removeFilters = () => setSearchParams(new URLSearchParams());

  const isFree = searchParams.get("is_free") == "true";

  const setIsFree = () => {
    if (isFree) {
      searchParams.delete("is_free");
    } else {
      searchParams.set("is_free", "true");
    }

    setSearchParams(searchParams);
  };
  return (
    <ModalProvider<boolean> initialState={false}>
      {({ modalState, closeModal, setModalState }) => (
        <>
          <CircleButton
            buttonType={"social media"}
            icon={<IconFilter className={"size-5 fill-on-primary"} />}
            onClick={() => setModalState(true)}
            className={"sm:hidden"}
          />
          <Dialog
            open={modalState}
            onClose={closeModal}
            transition
            className={cn(
              "fixed left-0 top-[5.625rem] z-10 h-[calc(100%)] w-full overflow-y-auto bg-black-1 text-on-primary transition data-[closed]:opacity-0",
              // top is dependent on header's height
              "max-lg:top-[5.5rem] max-sm:top-[4.5rem]",
              "data-[closed]:opacity-0",
              "data-[leave]:duration-300 data-[leave]:ease-in-out",
            )}
          >
            <div className={"flex h-full flex-col gap-6 overflow-y-auto rounded-[1.25rem] bg-surface-bright px-4 py-6"}>
              <div className={"flex items-center justify-between"}>
                <h2 className={"text-3xl/10 font-semibold"}>{t("General.filter")}</h2>
                <button
                  type={"button"}
                  aria-label={t("Actions.close")}
                  onClick={closeModal}
                  className={"rounded-full bg-surface-container-high p-2"}
                >
                  <IconX className={"size-6 stroke-on-primary"} />
                </button>
              </div>
              <div className={"space-y-4 pb-20"}>
                <div className={"space-y-6"}>
                  <FilterListbox
                    title={t("General.audio-language")}
                    values={[
                      { id: "uz", name: t("language-uz") },
                      { id: "ru", name: t("language-ru") },
                    ]}
                    paramName={"content_lang"}
                  />
                  {filters && filters.countries.length ? (
                    <FilterListbox
                      title={t("General.country")}
                      isSearch={true}
                      values={filters.countries}
                      paramName={"country"}
                    />
                  ) : null}
                  {filters && filters.genres.length ? (
                    <FilterListbox
                      title={t("General.genre", { count: 1 })}
                      isSearch={true}
                      values={filters.genres}
                      paramName={"genre"}
                    />
                  ) : null}
                  {filters && filters.years.length ? (
                    <FilterListbox
                      title={t("General.year")}
                      isSearch={true}
                      values={filters.years.map((year) => ({
                        id: year,
                        name: String(year),
                      }))}
                      paramName={"year"}
                    />
                  ) : null}
                  <FilterListbox
                    title={t("Actions.sort")}
                    values={[
                      { id: "rating", name: t("General.by-rating") },
                      { id: "data_created", name: t("General.by-upload-date") },
                    ]}
                    paramName={"ordering"}
                  />
                  {filters && filters.distributors.length ? (
                    <FilterListbox
                      title={t("General.distributor", { count: 5 })}
                      values={filters.distributors}
                      paramName={"distributor"}
                    />
                  ) : null}
                </div>
                <div className="flex flex-col gap-y-2.5">
                  <label className="block">Цена</label>
                  <Button
                    className="!mt-0 h-[3rem] w-full"
                    size="medium"
                    color={isFree ? "orange" : "white"}
                    onClick={setIsFree}
                  >
                    {t("free")}
                  </Button>
                </div>
                <hr className={"h-px w-full border-surface-container-high"} />
                <div className={"w-full space-y-4"}>
                  <div className={"grid grid-cols-2 gap-4"}>
                    <Button color={"black"} aria-label={t("Actions.reset")} onClick={removeFilters}>
                      {t("Actions.reset")}
                    </Button>
                    <Button aria-label={t("Actions.show")} onClick={closeModal}>
                      {t("Actions.show")}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </Dialog>
        </>
      )}
    </ModalProvider>
  );
};

export default FilterMobileMenu;
